import { createContext, ReactNode, useContext, useMemo } from "react";
import { intlLocale, type Language } from "../shared/localization";
import { ApiError } from "./api";
import { translations, type TranslationKey } from "./translations";

type Values = Record<string, string | number>;

function interpolate(template: string, values?: Values) {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    String(values[key] ?? `{${key}}`),
  );
}

type I18nValue = {
  language: Language;
  t: (key: TranslationKey, values?: Values) => string;
  errorMessage: (problem: unknown) => string;
  formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
  formatNumber: (value: number) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({
  language,
  children,
}: {
  language: Language;
  children: ReactNode;
}) {
  const value = useMemo<I18nValue>(() => {
    const dictionary = translations[language];
    const t = (key: TranslationKey, values?: Values) =>
      interpolate(dictionary[key], values);
    return {
      language,
      t,
      errorMessage(problem) {
        if (problem instanceof ApiError && problem.code) {
          const key = `error.${problem.code}` as TranslationKey;
          if (key in dictionary) return t(key);
        }
        return problem instanceof Error ? problem.message : t("error.REQUEST_FAILED");
      },
      formatDate(date, options) {
        const value = typeof date === "string" ? new Date(date) : date;
        return new Intl.DateTimeFormat(intlLocale(language), options).format(value);
      },
      formatNumber(number) {
        return new Intl.NumberFormat(intlLocale(language)).format(number);
      },
    };
  }, [language]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (value) return value;
  const dictionary = translations.ko;
  const t = (key: TranslationKey, values?: Values) => interpolate(dictionary[key], values);
  return {
    language: "ko" as const,
    t,
    errorMessage(problem: unknown) {
      return problem instanceof Error ? problem.message : t("error.REQUEST_FAILED");
    },
    formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions) {
      const target = typeof date === "string" ? new Date(date) : date;
      return new Intl.DateTimeFormat("ko-KR", options).format(target);
    },
    formatNumber(number: number) {
      return new Intl.NumberFormat("ko-KR").format(number);
    },
  };
}
